import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import sanskritPattern from '../assets/images/sanskrit-pattern.png';
import styles from '../styles/HeroSection.module.css';
import { publicApi } from '../api/api';

// Banner API row (banner_type = 'hero') -> slide shape used by the component below
const bannerToSlide = (b) => ({
    src: b.image_url,
    alt: b.title,
    badge: [b.badge_icon, b.badge_text].filter(Boolean).join(' '),
    title: b.headline_highlight ? `${b.title} ` : b.title,
    highlight: b.headline_highlight || '',
    description: b.description || '',
    features: Array.isArray(b.feature_chips) ? b.feature_chips : [],
    cta: b.cta_text && b.action_link ? { text: b.cta_text, route: b.action_link } : null
});

const HeroSection = () => {
    const navigate = useNavigate();
    const [slides, setSlides] = useState([]);
    const [current, setCurrent] = useState(0);
    const timeoutRef = useRef(null);

    // Load hero banners (banner_type = 'hero') from the API; nothing is shown when there are none
    useEffect(() => {
        let cancelled = false;
        publicApi
            .get('/content/banners')
            .then((res) => {
                const banners = res.data?.data?.banners;
                const heroSlides = (Array.isArray(banners) ? banners : [])
                    .filter((b) => b.banner_type === 'hero' && b.image_url)
                    .map(bannerToSlide);
                if (!cancelled) {
                    setSlides(heroSlides);
                    setCurrent(0);
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (slides.length < 2) return undefined;
        timeoutRef.current = setTimeout(() => {
            setCurrent((prev) => (prev + 1) % slides.length);
        }, 10000);
        return () => clearTimeout(timeoutRef.current);
    }, [current, slides.length]);

    const goToSlide = (idx) => setCurrent(idx);

    // Banner action_link is either an in-site path ("/landing/xyz") or a full URL
    const handleCTA = () => {
        const target = slides[current].cta?.route;
        if (!target) return;
        if (target.startsWith('/')) {
            navigate(target);
            return;
        }
        try {
            const url = new URL(target, window.location.origin);
            if (url.origin === window.location.origin) {
                navigate(url.pathname + url.search + url.hash);
            } else {
                window.location.href = url.href;
            }
        } catch (e) {
            // not a usable web target (e.g. an in-app screen name) - do nothing
        }
    };

    // No hero banners (or still loading / API failed): render no hero section at all
    if (slides.length === 0) return null;

    return (
        <section className={styles.heroModern}>
            {/* Slider Background - Removed background images for better readability */}
            <div className={styles.heroSliderBg}>
                {slides.map((slide, idx) => (
                    <div
                        key={idx}
                        className={`${styles.heroSlide} ${idx === current ? styles.active : ''}`}
                        style={{
                            background: 'transparent',
                        }}
                    />
                ))}
                <div className={styles.heroOverlay}></div>
            </div>

            {/* Pattern Overlay */}
            <div className={styles.heroPatternOverlay}>
                <div 
                    className={styles.heroPattern}
                    style={{
                        backgroundImage: `url(${sanskritPattern})`,
                    }}
                ></div>
            </div>

            {/* Content */}
            <div className="container">
                <div className={styles.heroWrapper}>
                    <div className={styles.heroContent}>
                        {slides[current].badge && (
                            <div className={styles.heroBadge}>
                                {slides[current].badge}
                            </div>
                        )}
                        <h1 className={styles.heroTitle}>
                            {slides[current].title}
                            <span className={styles.heroHighlight}>{slides[current].highlight}</span>
                        </h1>
                        <p className={styles.heroDescription}>
                            {slides[current].description}
                        </p>
                        <div className={styles.heroFeatures}>
                            {slides[current].features.map((feature, idx) => (
                                <div key={idx} className={styles.heroFeature}>
                                    <div className={styles.featureIcon}>{feature.icon}</div>
                                    <span>{feature.text}</span>
                                </div>
                            ))}
                        </div>
                        {slides[current].cta && (
                            <div className={styles.heroButtons}>
                                <button 
                                    className={`btn ${styles.heroBtnPrimary} ${styles.heroCtaSingle}`}
                                    onClick={handleCTA}
                                >
                                    {slides[current].cta.text}
                                </button>
                            </div>
                        )}
                    </div>
                    <div className={styles.heroVisual}>
                        <div className={styles.heroImageContainer}>
                            <img 
                                src={slides[current].src} 
                                alt={slides[current].alt}
                                className={styles.heroImage}
                            />
                            <div className={styles.heroDecorations}>
                                <div className={`${styles.decoration} ${styles.decoration1}`}></div>
                                <div className={`${styles.decoration} ${styles.decoration2}`}></div>
                                <div className={`${styles.decoration} ${styles.decoration3}`}></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Slider Dots */}
            {slides.length > 1 && <div className={styles.heroSliderDots}>
                {slides.map((_, idx) => (
                    <button
                        key={idx}
                        className={`${styles.sliderDot} ${idx === current ? styles.active : ''}`}
                        onClick={() => goToSlide(idx)}
                        aria-label={`Go to slide ${idx + 1}`}
                    />
                ))}
            </div>}
        </section>
    );
};

export default HeroSection;
